import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import Header from '../components/layout/Header';
import { escritorioDisponible, pedirPermisoEscritorio, permisoEscritorio, sonar } from '../lib/avisos';

/**
 * Preferencias de notificación del usuario.
 *
 * Siempre las propias: el endpoint no acepta un id, así que nadie configura
 * las de otro. Por eso la pantalla no pregunta "de quién".
 */
export default function NotificationSettings() {
    const qc = useQueryClient();
    const [draft, setDraft] = useState(null);

    const { data: resp, isLoading } = useQuery({
        queryKey: ['notification-preferences'],
        queryFn: () => axios.get('/api/notification-preferences'),
    });

    useEffect(() => {
        if (!resp?.data) return;
        const d = resp.data;
        setDraft({
            types: d.types && !Array.isArray(d.types) ? d.types : {},
            sounds: d.sounds && !Array.isArray(d.sounds) ? d.sounds : {},
            desktop: d.desktop !== false,
            quiet_hours: d.quiet_hours || { enabled: false, from: '22:00', to: '08:00' },
            only_mine: Boolean(d.only_mine),
            min_lead_score: d.min_lead_score ?? '',
        });
    }, [resp]);

    const save = useMutation({
        mutationFn: (payload) => axios.put('/api/notification-preferences', payload),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-preferences'] }),
    });

    const catalog = resp?.data?.catalog || {};

    // Se agrupan por origen para que la lista no sea siete interruptores sueltos.
    const groups = useMemo(() => {
        const out = {};
        Object.entries(catalog).forEach(([key, meta]) => {
            const g = meta.group || 'Otros';
            (out[g] ||= []).push({ key, ...meta });
        });
        return out;
    }, [catalog]);

    if (isLoading || !draft) {
        return (
            <div className="flex-1 flex flex-col overflow-hidden">
                <Header title="Notificaciones" />
                <p className="p-6 text-sm text-gray-400">Cargando…</p>
            </div>
        );
    }

    // Ausencia = habilitado, igual que en el backend: un tipo que nunca se tocó
    // debe verse encendido.
    const isOn = (key) => draft.types[key] !== false;

    const conSonido = (key) => draft.sounds[key] !== false;

    const toggleSonido = (key) => {
        const next = { ...draft, sounds: { ...draft.sounds, [key]: !conSonido(key) } };
        setDraft(next);
        save.mutate({ sounds: next.sounds });
    };

    const toggle = (key) => {
        const next = { ...draft, types: { ...draft.types, [key]: !isOn(key) } };
        setDraft(next);
        save.mutate({ types: next.types });
    };

    const patch = (partial) => {
        const next = { ...draft, ...partial };
        setDraft(next);
        save.mutate(partial);
    };

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            <Header title="Notificaciones" />

            <div className="flex-1 overflow-y-auto p-6 max-w-3xl w-full mx-auto space-y-6">
                <p className="text-sm text-gray-500">
                    Elegí qué avisos querés recibir. Es tu configuración personal: no afecta a nadie
                    más del equipo. Los cambios se guardan solos.
                </p>

                {Object.entries(groups).map(([group, items]) => (
                    <section key={group} className="bg-white dark:bg-[#1a100c] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                        <h2 className="px-4 py-2.5 text-sm font-semibold dark:text-white border-b border-gray-100 dark:border-gray-800">
                            {group}
                        </h2>
                        <div className="divide-y divide-gray-100 dark:divide-gray-800">
                            {items.map((item) => (
                                <label key={item.key} className="px-4 py-3 flex items-center gap-3 cursor-pointer">
                                    <span className={`material-symbols-outlined text-lg shrink-0 ${
                                        isOn(item.key) ? 'text-primary' : 'text-gray-300 dark:text-gray-700'
                                    }`}>
                                        {item.icon || 'notifications'}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium dark:text-white">{item.label}</p>
                                        <p className="text-xs text-gray-400">{item.description}</p>
                                    </div>
                                    {item.sound && (
                                        <button
                                            type="button"
                                            onClick={(e) => { e.preventDefault(); toggleSonido(item.key); }}
                                            disabled={!isOn(item.key)}
                                            title={conSonido(item.key) ? 'Con sonido. Clic para silenciar.' : 'Sin sonido. Clic para activarlo.'}
                                            aria-pressed={conSonido(item.key)}
                                            className={`shrink-0 size-8 rounded-lg flex items-center justify-center transition-colors disabled:opacity-30 ${
                                                conSonido(item.key)
                                                    ? 'text-primary bg-primary/10 hover:bg-primary/15'
                                                    : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                                            }`}
                                        >
                                            <span className="material-symbols-outlined text-[18px]">
                                                {conSonido(item.key) ? 'volume_up' : 'volume_off'}
                                            </span>
                                        </button>
                                    )}
                                    <input
                                        type="checkbox"
                                        checked={isOn(item.key)}
                                        onChange={() => toggle(item.key)}
                                        className="shrink-0 w-4 h-4 rounded"
                                    />
                                </label>
                            ))}
                        </div>
                    </section>
                ))}

                <AvisosDelNavegador
                    activo={draft.desktop}
                    onCambiar={(desktop) => patch({ desktop })}
                />

                {/* Filtros que aplican a todo */}
                <section className="bg-white dark:bg-[#1a100c] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                    <h2 className="px-4 py-2.5 text-sm font-semibold dark:text-white border-b border-gray-100 dark:border-gray-800">
                        Filtros generales
                    </h2>

                    <label className="px-4 py-3 flex items-center gap-3 cursor-pointer border-b border-gray-100 dark:border-gray-800">
                        <span className="material-symbols-outlined text-lg text-gray-400 shrink-0">person_pin</span>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium dark:text-white">Solo lo mío</p>
                            <p className="text-xs text-gray-400">
                                Recibir únicamente avisos de contactos asignados a vos.
                            </p>
                        </div>
                        <input
                            type="checkbox"
                            checked={draft.only_mine}
                            onChange={(e) => patch({ only_mine: e.target.checked })}
                            className="shrink-0 w-4 h-4 rounded"
                        />
                    </label>

                    <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <span className="material-symbols-outlined text-lg text-gray-400 shrink-0">bedtime</span>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium dark:text-white">Horario de silencio</p>
                                <p className="text-xs text-gray-400">
                                    En ese rango no se genera ninguna notificación.
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                checked={draft.quiet_hours.enabled}
                                onChange={(e) => patch({ quiet_hours: { ...draft.quiet_hours, enabled: e.target.checked } })}
                                className="shrink-0 w-4 h-4 rounded"
                            />
                        </label>

                        {draft.quiet_hours.enabled && (
                            <div className="mt-3 ml-8 flex items-center gap-2">
                                <input
                                    type="time"
                                    value={draft.quiet_hours.from || '22:00'}
                                    onChange={(e) => patch({ quiet_hours: { ...draft.quiet_hours, from: e.target.value } })}
                                    className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white"
                                />
                                <span className="text-xs text-gray-400">a</span>
                                <input
                                    type="time"
                                    value={draft.quiet_hours.to || '08:00'}
                                    onChange={(e) => patch({ quiet_hours: { ...draft.quiet_hours, to: e.target.value } })}
                                    className="text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white"
                                />
                            </div>
                        )}
                    </div>

                    <div className="px-4 py-3">
                        <div className="flex items-center gap-3">
                            <span className="material-symbols-outlined text-lg text-gray-400 shrink-0">speed</span>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium dark:text-white">Puntaje mínimo del lead</p>
                                <p className="text-xs text-gray-400">
                                    No avisar por leads que la IA puntuó por debajo de este valor.
                                </p>
                            </div>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                placeholder="—"
                                value={draft.min_lead_score}
                                onChange={(e) => setDraft({ ...draft, min_lead_score: e.target.value })}
                                onBlur={(e) => patch({
                                    min_lead_score: e.target.value === '' ? null : Number(e.target.value),
                                })}
                                className="shrink-0 w-20 text-sm p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white"
                            />
                        </div>
                        <p className="text-[11px] text-gray-400 mt-2 ml-8">
                            Un lead recién entrado todavía no tiene puntaje, así que este filtro no lo
                            silencia: sin esa excepción se perdería justo el aviso más importante.
                        </p>
                    </div>
                </section>
            </div>
        </div>
    );
}

/**
 * Lo que depende de este navegador: el permiso de notificaciones de escritorio
 * lo da cada navegador por separado, así que se muestra su estado real.
 */
function AvisosDelNavegador({ activo, onCambiar }) {
    const [permiso, setPermiso] = useState(permisoEscritorio());

    const activar = async () => {
        if (permiso === 'default') {
            setPermiso(await pedirPermisoEscritorio());
        }
        onCambiar(true);
    };

    const estado = !escritorioDisponible()
        ? 'Este navegador no muestra notificaciones de escritorio.'
        : permiso === 'denied'
            ? 'El navegador las tiene bloqueadas para este sitio: se habilitan desde el candado de la barra de direcciones.'
            : permiso === 'default'
                ? 'Falta darle permiso al navegador.'
                : 'Permiso concedido.';

    return (
        <section className="bg-white dark:bg-[#1a100c] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
            <h2 className="px-4 py-2.5 text-sm font-semibold dark:text-white border-b border-gray-100 dark:border-gray-800">
                En este navegador
            </h2>

            <div className="px-4 py-3 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800">
                <span className="material-symbols-outlined text-lg text-gray-400 shrink-0">desktop_windows</span>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium dark:text-white">Notificaciones de escritorio</p>
                    <p className="text-xs text-gray-400">
                        Aparecen en la esquina de la pantalla cuando el CRM está en otra pestaña o minimizado. {estado}
                    </p>
                </div>
                {activo && permiso === 'default' ? (
                    <button
                        type="button"
                        onClick={activar}
                        className="shrink-0 text-xs font-bold text-white bg-primary hover:bg-primary/90 px-3 py-1.5 rounded-lg"
                    >
                        Dar permiso
                    </button>
                ) : (
                    <input
                        type="checkbox"
                        checked={activo}
                        disabled={!escritorioDisponible()}
                        onChange={(e) => (e.target.checked ? activar() : onCambiar(false))}
                        className="shrink-0 w-4 h-4 rounded"
                    />
                )}
            </div>

            <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
                <span className="material-symbols-outlined text-lg text-gray-400 shrink-0">music_note</span>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium dark:text-white">Probar sonidos</p>
                    <p className="text-xs text-gray-400">Son los mismos que suenan en la app del teléfono.</p>
                </div>
                <button type="button" onClick={() => sonar('lead_nuevo')}
                    className="shrink-0 text-xs font-bold text-primary bg-primary/10 hover:bg-primary/15 px-3 py-1.5 rounded-lg flex items-center gap-1">
                    <span className="material-symbols-outlined text-[16px]">play_arrow</span> Lead nuevo
                </button>
                <button type="button" onClick={() => sonar('lead_asignado')}
                    className="shrink-0 text-xs font-bold text-primary bg-primary/10 hover:bg-primary/15 px-3 py-1.5 rounded-lg flex items-center gap-1">
                    <span className="material-symbols-outlined text-[16px]">play_arrow</span> Asignado o escalado
                </button>
            </div>
        </section>
    );
}
